/**
 * About page — Company story, mission, values, and roadmap teaser.
 *
 * All colours use CSS variables for proper light/dark theme support.
 */

import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Target, Heart, Lightbulb, Rocket } from 'lucide-react'

export function About() {
  useEffect(() => {
    document.title = 'About — bigflowdata'
  }, [])

  return (
    <div className="pt-16">
      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-brand-500/20 rounded-full blur-[128px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-black mb-6">
              We believe small stores deserve
              <span className="gradient-text"> big-store intelligence</span>
            </h1>
            <p className="text-xl text-[var(--color-text-secondary)]">
              bigflowdata builds focused analytics tools that help Shopify
              merchants understand their customers and make decisions based
              on real data — not gut feeling.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          MISSION
          ============================================================ */}
      <section className="py-20 lg:py-24 section-alt">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-[var(--color-text-secondary)]">
                <p>
                  bigflowdata started with a simple observation: most Shopify
                  merchants have access to incredible customer data, but lack
                  the tools to turn it into actionable insights.
                </p>
                <p>
                  Large retailers spend millions on data teams and
                  recommendation engines. Independent store owners? They're
                  left guessing which products to promote, which bundles to
                  create, and what to recommend on product pages.
                </p>
                <p>
                  We're building tools that close that gap — giving every
                  merchant, regardless of size, the power to make data-driven
                  decisions that grow revenue.
                </p>
              </div>
            </div>

            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)]">
              <h3 className="text-lg font-bold mb-6 text-center">Our Mission</h3>
              <p className="text-center text-[var(--color-text-secondary)] text-lg leading-relaxed italic">
                "Make data-driven intelligence accessible to every
                e-commerce merchant, not just the ones with data
                science teams."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          VALUES
          ============================================================ */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Our Values</h2>
            <p className="text-[var(--color-text-secondary)] text-lg max-w-2xl mx-auto">
              These principles shape every product we build and every
              decision we make.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)] text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Target className="h-8 w-8 text-brand-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Simplicity</h3>
              <p className="text-[var(--color-text-muted)]">
                Powerful tools shouldn't require a data science degree. If
                it's not simple enough for a busy merchant to use in minutes,
                it's not done.
              </p>
            </div>

            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)] text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Heart className="h-8 w-8 text-brand-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Transparency</h3>
              <p className="text-[var(--color-text-muted)]">
                Honest pricing with no hidden fees. Clear communication about
                what our tools do and don't do. No dark patterns, ever.
              </p>
            </div>

            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)] text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Lightbulb className="h-8 w-8 text-brand-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Innovation</h3>
              <p className="text-[var(--color-text-muted)]">
                We continuously improve based on real merchant feedback and
                the latest in data science — so our tools keep getting
                smarter, and so do your recommendations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          ROADMAP TEASER
          ============================================================ */}
      <section className="py-20 lg:py-24 section-alt">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
            <Rocket className="h-8 w-8 text-brand-500" />
          </div>
          <h2 className="text-3xl font-bold mb-4">pairs is just the beginning</h2>
          <p className="text-[var(--color-text-secondary)] text-lg max-w-2xl mx-auto mb-8">
            Product recommendations are our first tool, but we're building a
            full suite of analytics products for Shopify merchants. Each one
            designed to solve a specific problem, simply and effectively.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-brand-500 hover:text-brand-400 font-medium transition-colors"
          >
            Want to shape what we build next? Get in touch
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ============================================================
          CTA
          ============================================================ */}
      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to see what your data reveals?
          </h2>
          <p className="text-xl text-[var(--color-text-secondary)] mb-10">
            Join merchants who are already using data to make smarter
            decisions and grow their stores.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/pairs"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-2xl transition-all hover:scale-105"
            >
              Explore bigflow.pairs
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]/20 font-semibold rounded-2xl transition-all"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
